import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import messages from '../../locale/messages';
import Link from '../Link';
import EstimatorDropdown from '../EstimatorDropdown/EstimatorDropdown';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';


import HeaderModal from '../HeaderModal/HeaderModal';
import RegisterForm from '../Login/RegisterForm/RegisterForm';
import CommonModal from '../Modal/CommonModal';
import LoginForm from '../Login/LoginForm/LoginForm';
import VerifyPhoneNumberForm from '../Login/VerifyPhoneNumberForm/VerifyPhoneNumberForm';
import Logout from '../Logout/Logout';

import { closeHeaderModal, openHeaderModal } from '../../actions/siteadmin/modalActions';
import { userLogout } from '../../actions/user/userLogout';
import { choseToTheme } from '../../actions/currency/getCurrencyRates';
import { setRuntimeVariable } from '../../actions/runtime';

import { api, logoUploadDir } from '../../config';
import history from '../../history';
import { isRTL } from '../../helpers/formatLocale';
import { CloseSvgIcon, HamburgSvgIcon } from '../Common/Assets/SVGIcon/CommonSvgIcon';
import { removeCookies } from '../../helpers/Cookies/cookies';

import HeaderSideList from './HeaderSideList';
import s from './Header.css';

const Header = (props) => {
  const { darkHome, socket, companyMode } = props;
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl()
  const logo = useSelector(state => state?.siteSettings?.data?.homeLogo);
  const logoText = useSelector(state => state?.siteSettings?.data?.logoText);
  const siteName = useSelector(state => state?.siteSettings?.data?.siteName);
  const currentLocale = useSelector(state => state?.intl?.locale);
  const loginModal = useSelector(state => state?.adminModalStatus?.loginModal);
  const verifyPhoneNumber = useSelector(state => state?.adminModalStatus?.verifyPhoneNumber);
  const registerForm = useSelector(state => state?.adminModalStatus?.registerForm);
  const userLogoutModal = useSelector((state) => state?.adminModalStatus?.userLogout);
  const userId = useSelector(state => state?.userAccount?.data?.userId);
  const userToken = useSelector(state => state?.userAccount?.data?.key);
  const location = history?.location?.pathname || '';
  // Company surfaces (/company/*, /become-a-company) hide the homeowner-only CTAs
  // (job-price estimator + Login/Sign up) so a company user isn't shown client nav.
  const isCompanyArea = !!companyMode || (!!location && (/^\/company(\/|$)/.test(location) || location === '/become-a-company'));

  const handleMenuOpen = () => {
    setIsOpen(true);
    if (typeof window !== 'undefined') {
      document.body.classList.add('sideMenuBodyHidden');
    }
  }
  const handleMenuClose = () => {
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      document.body.classList.remove('sideMenuBodyHidden');
    }
  }

  const handleLanguageChange = () => {
    dispatch(openHeaderModal('languageModal'));
    handleMenuClose()
  };
  const handleThemeChange = useCallback((theme) => {
    dispatch(choseToTheme(theme));
    handleMenuClose();
  }, [dispatch]);


  const openLoginModal = () => {
    dispatch(openHeaderModal('loginModal'));
    handleMenuClose()
  }
  const closeVerifyPhoneModal = () => {
    dispatch(closeHeaderModal('verifyPhoneNumber'));
    dispatch(closeHeaderModal('loginModal'));
  }

  const closeLoginModal = () => {
    dispatch(closeHeaderModal('loginModal'));
  }

  const closeRegisterModal = () => {
    dispatch(closeHeaderModal('registerForm'));
    dispatch(closeHeaderModal('verifyPhoneNumber'));
    dispatch(closeHeaderModal('loginModal'));
  }

  const closeLogoutModal = () => {
    dispatch(closeHeaderModal('userLogout'))
  }

  useEffect(() => {
    if (socket && userId) {
      socket.removeListener('loginCheck-' + userId);
      socket.removeListener('partnerApprove-' + userId);
    }
  }, [userId, socket])

  // Login-with-return: a guarded route (e.g. /order/:id hit while logged-out) redirects here as
  // /?login=1&return=<encoded path>. Stash the return path as referUrl (the same runtime variable
  // CategorySearch uses, consumed post-OTP by verifyOtp -> history.push(referUrl)), open the login
  // modal, and strip the query so a manual refresh of "/" doesn't keep re-popping the modal.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (userId) return; // already logged in — nothing to do
    let params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (params.get('login') !== '1') return;
    const returnPath = params.get('return');
    if (returnPath) {
      dispatch(setRuntimeVariable({ name: 'referUrl', value: returnPath }));
    }
    dispatch(openHeaderModal('loginModal'));
    // Clean the URL (keep any other query params) without adding a history entry.
    params.delete('login');
    params.delete('return');
    const rest = params.toString();
    const cleanUrl = window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash;
    try { window.history.replaceState(null, '', cleanUrl); } catch (e) { /* ignore */ }
  }, []); // run once on mount


  useEffect(() => {
    if (socket && userId) {
      socket.on('loginCheck-' + userId, (response) => {
        let socketValue = JSON.parse(response);
        const authToken = socketValue?.data?.data?.token;
        if (authToken === userToken) {
          dispatch(closeHeaderModal('userLogout'))
          dispatch(userLogout());
        }
      });
      socket.on('deleteUser-' + userId, (data) => {
        dispatch(userLogout());
      });
      socket.on('partnerApprove-' + userId, (data) => {
        dispatch(userLogout());
      });
      socket.on(`webLoginCheck-${userId}`, (data) => {

        if (typeof window !== "undefined") {
          removeCookies({ name: "id_token" })
          window.location.assign('/');
        }

      });
    }

  }, [socket, userId, dispatch])

  return (
    <>
      <CommonModal
        title={registerForm ? formatMessage(messages.signupTitle) : (verifyPhoneNumber ? formatMessage(messages.verifyNumberLabel) : formatMessage(messages.loginSignupLabel))}
        onHide={registerForm ? closeRegisterModal : (verifyPhoneNumber ? closeVerifyPhoneModal : closeLoginModal)}
        modalStatus={registerForm ? registerForm : (verifyPhoneNumber ? verifyPhoneNumber : loginModal)} >
        {registerForm ? <RegisterForm /> : (verifyPhoneNumber ? <VerifyPhoneNumberForm /> : <LoginForm />)}
      </CommonModal>
      <HeaderModal />
      <CommonModal
        title={formatMessage(messages.logout)}
        onHide={closeLogoutModal}
        modalStatus={userLogoutModal} >
        <Logout />
      </CommonModal>
      <header className={cx(s.frontPageHeader, darkHome,)}>
        <Link className={cx(logo ? s.headerLogoContainer : s.headerLogoTextContainer,logoText && s.headerBrandLogoWithTextContainer)} to="/">
          {logo ? <img src={api.apiEndpoint + logoUploadDir + logo} alt={siteName} className={cx(s.headerBrandLogo, 'headerBrandLogoRTL')} /> : <span>{siteName}</span>}
          {logoText && <span className={s.logoBrandNameText}>{logoText}</span>}
        </Link>
        <button type="button" aria-label="Open menu" className={s.headerHamburgBtn} onClick={handleMenuOpen}>
          <HamburgSvgIcon />
        </button>

        {/* mobileViewOffCanvas */}
        <>
          <div className={cx(s.headerListWebHidden, s.headerMenuOffcanvasBackShadow, isOpen && s.headerMenuBackShadowShow)} onClick={handleMenuClose} />
          <div className={cx(s.mobileViewHeaderMenuOffCanvas, s.headerListWebHidden,
            (isOpen && s.homeHeaderOffcanvasShow), (isRTL(currentLocale) ? s.homeHeaderOffcanvasEnd : s.homeHeaderOffcanvasStart))} >
            <button aria-label="Close menu" className={cx(s.homeHeaderCloseBtn)} onClick={handleMenuClose}>
              <CloseSvgIcon />
            </button>
            <HeaderSideList
              handleThemeChange={handleThemeChange}
              openLoginModal={openLoginModal}
              companyMode={isCompanyArea}
              containerClass={cx(s.headerMenuListWrapper, s.verticalHeaderMenuListWrapper)}
              handleLanguageChange={handleLanguageChange}
              handleMenuClose={handleMenuClose}
              socket={socket}
            />
          </div>
        </>
        {/* webViewMenuList */}

        {!isCompanyArea && <div className={s.estimatorSlot}><EstimatorDropdown /></div>}
        {/* Two-path CTAs next to login (desktop only — on mobile they live at the
            bottom of the estimator panel). Book now = small jobs · Get a quote = large. */}
        {!isCompanyArea && (
          <div className={s.headerCtas}>
            <a className={s.headerBtnBook} href="/category?type=all">Book now</a>
            <a className={s.headerBtnQuote} href="/post-a-project">Get a quote</a>
          </div>
        )}
        <HeaderSideList
          handleThemeChange={handleThemeChange}
          openLoginModal={openLoginModal}
          companyMode={isCompanyArea}
          containerClass={cx(s.headerListMobileHidden, s.headerMenuListWrapper)}
          handleLanguageChange={handleLanguageChange}
          socket={socket}
        />
      </header>
    </>
  );
};



export default withStyles(s)(Header);
